/**
 * AccountApiKeysDialog — manage the N api-keys of one account (list / create /
 * edit / rotate / revoke / reactivate / delete), backed by the dedicated
 * /account/{id}/api-keys endpoints (admin, SuperUser only).
 *
 * Each key is optionally bound to a single application; a bound key pins the
 * app of the send, an unbound ("whole account") key works for every app.
 *
 * The plaintext secret is shown ONCE after create/rotate via a reveal dialog
 * and never persisted — it lives only in component state until dismissed.
 */

import React, { useEffect, useState } from "react";
import { useNotify, useTranslate } from "react-admin";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  VpnKeyOutlined as KeyIcon,
  AddCircleOutline as AddIcon,
  EditOutlined as EditIcon,
  Autorenew as RotateIcon,
  BlockOutlined as RevokeIcon,
  CheckCircleOutline as ReactivateIcon,
  DeleteOutlined as DeleteIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import { useCanManageAccounts } from "@/utils/permissions";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import {
  AccountApiKey,
  AccountApiKeyPayload,
  ApiKeyScope,
  LIMITED_SCOPES,
  createAccountApiKey,
  deleteAccountApiKey,
  listAccountApiKeys,
  rotateAccountApiKey,
  joinScopes,
  parseScopes,
  setAccountApiKeyActive,
  updateAccountApiKey,
} from "../../services/accountApiKeyService";

const i18n = "resources.accounts.apiKeysDialog";

/** Application option for the create/edit picker. */
export interface AccountApplicationOption {
  id: string;
  name: string;
}

export interface AccountApiKeysDialogProps {
  accountId: string;
  accountName: string;
  /** Applications of THIS account (id + name), for the per-key app picker. */
  applications: AccountApplicationOption[];
  open: boolean;
  onClose: () => void;
}

interface EditorState {
  keyId: string | null; // null = create
  label: string;
  applicationId: string; // "" = whole account
  /** Los ambitos marcados. "manage" excluye a los demas: ya lo abre todo. */
  scopes: string[];
  isTest: boolean;
}

const emptyEditor = (): EditorState => ({
  keyId: null,
  label: "",
  applicationId: "",
  scopes: ["send"],
  isTest: false,
});

/**
 * Marcar y desmarcar un ambito. "manage" y los limitados se excluyen: una key con
 * "manage" ya llega a todo, asi que sumarle "envio" no significa nada y leerlo en la
 * lista haria pensar que esta MAS limitada de lo que esta.
 */
const toggleScope = (scopes: string[], scope: string, marcado: boolean): string[] => {
  if (!marcado) return scopes.filter((s) => s !== scope);
  return scope === "manage" ? ["manage"] : [...scopes.filter((s) => s !== "manage"), scope];
};

export const AccountApiKeysDialog: React.FC<AccountApiKeysDialogProps> = ({
  accountId,
  accountName,
  applications,
  open,
  onClose,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const canEdit = useCanManageAccounts();
  const theme = useTheme();
  const rg = theme.custom.rg;

  const [loading, setLoading] = useState(false);
  const [keys, setKeys] = useState<AccountApiKey[]>([]);
  const [busy, setBusy] = useState(false);

  const [editor, setEditor] = useState<EditorState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AccountApiKey | null>(null);
  const [revealed, setRevealed] = useState<{ label: string; apiKey: string } | null>(
    null,
  );

  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`${i18n}.${key}`, options);

  const load = React.useCallback(() => {
    setLoading(true);
    listAccountApiKeys(accountId)
      .then(setKeys)
      .catch((err: unknown) => {
        notify(extractErrorMessage(err, "ra.notification.http_error", translate), {
          type: "error",
        });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  useEffect(() => {
    if (!open) return;
    setEditor(null);
    setConfirmDelete(null);
    setRevealed(null);
    load();
  }, [open, load]);

  const appLabel = (k: AccountApiKey): string =>
    k.applicationName ?? t("wholeAccount");

  const openCreate = () => setEditor(emptyEditor());
  const openEdit = (k: AccountApiKey) =>
    setEditor({
      keyId: k.id,
      label: k.label,
      applicationId: k.applicationId ?? "",
      scopes: parseScopes(k.scope),
      isTest: k.isTest,
    });

  const save = async (): Promise<void> => {
    if (!editor) return;
    const payload: AccountApiKeyPayload = {
      label: editor.label.trim(),
      applicationId: editor.applicationId || null,
      scope: joinScopes(editor.scopes),
      isTest: editor.isTest,
    };
    setBusy(true);
    try {
      if (editor.keyId === null) {
        const created = await createAccountApiKey(accountId, payload);
        notify(t("created"), { type: "success" });
        setEditor(null);
        setRevealed({ label: created.label, apiKey: created.apiKey });
        load();
      } else {
        await updateAccountApiKey(editor.keyId, payload);
        notify(t("updated"), { type: "success" });
        setEditor(null);
        load();
      }
    } catch (err: unknown) {
      notify(extractErrorMessage(err, "ra.notification.http_error", translate), {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const rotate = async (k: AccountApiKey): Promise<void> => {
    setBusy(true);
    try {
      const rotated = await rotateAccountApiKey(k.id);
      notify(t("rotated"), { type: "success" });
      setRevealed({ label: rotated.label, apiKey: rotated.apiKey });
      load();
    } catch (err: unknown) {
      notify(extractErrorMessage(err, "ra.notification.http_error", translate), {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (k: AccountApiKey): Promise<void> => {
    setBusy(true);
    try {
      await setAccountApiKeyActive(k.id, !k.isActive);
      notify(t(k.isActive ? "revokedMsg" : "reactivated"), { type: "success" });
      load();
    } catch (err: unknown) {
      notify(extractErrorMessage(err, "ra.notification.http_error", translate), {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (): Promise<void> => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      await deleteAccountApiKey(confirmDelete.id);
      notify(t("deleted"), { type: "success" });
      setConfirmDelete(null);
      load();
    } catch (err: unknown) {
      notify(extractErrorMessage(err, "ra.notification.http_error", translate), {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const copyRevealed = async (): Promise<void> => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed.apiKey);
      notify(t("copied"), { type: "success" });
    } catch {
      /* clipboard unavailable — the value is still on screen */
    }
  };

  const headCellSx = {
    fontSize: 11,
    fontWeight: 700,
    color: rg.text3,
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
    borderBottom: `1px solid ${rg.border}`,
    py: 1,
  };
  const bodyCellSx = {
    fontSize: 12.5,
    borderBottom: `1px solid ${rg.border}`,
    py: 1,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <KeyIcon sx={{ fontSize: 18, color: rg.text3 }} />
          <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: "-0.02em" }}>
            {t("title", { name: accountName })}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: "20px",
            py: "10px",
          }}
        >
          <Typography sx={{ fontSize: "12px", color: rg.text3 }}>
            {t("hint")}
          </Typography>
          {canEdit && (
            <Button
              startIcon={<AddIcon sx={{ fontSize: 17 }} />}
              variant="contained"
              size="small"
              onClick={openCreate}
              sx={{ flexShrink: 0 }}
            >
              {t("new")}
            </Button>
          )}
        </Box>

        {loading && (
          <Box sx={{ display: "grid", placeItems: "center", py: 5 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {!loading && keys.length === 0 && (
          <Typography
            sx={{ py: 4, textAlign: "center", fontSize: "12.5px", color: rg.text3 }}
          >
            {t("empty")}
          </Typography>
        )}

        {!loading && keys.length > 0 && (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>{t("colLabel")}</TableCell>
                  <TableCell sx={headCellSx}>{t("colApplication")}</TableCell>
                  <TableCell sx={headCellSx}>{t("colScope")}</TableCell>
                  <TableCell sx={headCellSx}>{t("colTest")}</TableCell>
                  <TableCell sx={headCellSx}>{t("colStatus")}</TableCell>
                  {canEdit && (
                    <TableCell align="right" sx={headCellSx}>
                      {t("colActions")}
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {keys.map((k) => (
                  <TableRow key={k.id} hover>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
                      {k.label || "—"}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 12.5,
                          color: k.applicationName ? rg.text : rg.text3,
                          fontStyle: k.applicationName ? "normal" : "italic",
                        }}
                      >
                        {appLabel(k)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {parseScopes(k.scope).map((s) => (
                          <Chip
                            key={s}
                            size="small"
                            // Un ambito que no conocemos se ensena tal cual en vez de
                            // dejar la celda vacia: es una key que sigue funcionando.
                            label={t(`scope.${s}`, { _: s })}
                            sx={{ fontSize: 10.5, height: 20 }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {k.isTest ? (
                        <Chip
                          size="small"
                          label={t("testBadge")}
                          sx={{
                            fontSize: 10.5,
                            height: 20,
                            backgroundColor: rg.warn.bg,
                            color: rg.warn.fg,
                          }}
                        />
                      ) : (
                        <Typography component="span" sx={{ fontSize: 12.5, color: rg.text3 }}>
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Chip
                        size="small"
                        label={t(k.isActive ? "active" : "revoked")}
                        sx={{
                          fontSize: 10.5,
                          height: 20,
                          backgroundColor: k.isActive ? rg.ok.bg : rg.danger.bg,
                          color: k.isActive ? rg.ok.fg : rg.danger.fg,
                        }}
                      />
                    </TableCell>
                    {canEdit && (
                      <TableCell align="right" sx={bodyCellSx}>
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.25 }}>
                          <Tooltip title={translate("ra.action.edit")}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={busy}
                                onClick={() => openEdit(k)}
                              >
                                <EditIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={t("rotate")}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={busy}
                                onClick={() => void rotate(k)}
                              >
                                <RotateIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={t(k.isActive ? "revoke" : "reactivate")}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={busy}
                                onClick={() => void toggleActive(k)}
                                sx={{ color: k.isActive ? rg.danger.line : rg.ok.line }}
                              >
                                {k.isActive ? (
                                  <RevokeIcon sx={{ fontSize: 17 }} />
                                ) : (
                                  <ReactivateIcon sx={{ fontSize: 17 }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={translate("ra.action.delete")}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={busy}
                                onClick={() => setConfirmDelete(k)}
                                sx={{ color: rg.danger.line }}
                              >
                                <DeleteIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>

      {/* Create / Edit editor */}
      <Dialog
        open={editor !== null}
        onClose={() => !busy && setEditor(null)}
        onClick={(e) => e.stopPropagation()}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>
          {editor?.keyId === null ? t("createTitle") : t("editTitle")}
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label={t("labelField")}
            value={editor?.label ?? ""}
            onChange={(e) =>
              setEditor((prev) => (prev ? { ...prev, label: e.target.value } : prev))
            }
            size="small"
            fullWidth
            autoFocus
          />
          <TextField
            select
            label={t("applicationField")}
            value={editor?.applicationId ?? ""}
            onChange={(e) =>
              setEditor((prev) =>
                prev ? { ...prev, applicationId: e.target.value } : prev,
              )
            }
            size="small"
            fullWidth
          >
            <MenuItem value="">{t("wholeAccountOption")}</MenuItem>
            {applications.map((app) => (
              <MenuItem key={app.id} value={app.id}>
                {app.name}
              </MenuItem>
            ))}
          </TextField>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
              {t("scopeField")}
            </Typography>
            {([...LIMITED_SCOPES, "manage"] as ApiKeyScope[]).map((scope) => (
              <FormControlLabel
                key={scope}
                sx={{ display: "flex", alignItems: "flex-start", ml: 0, mb: 0.5 }}
                control={
                  <Checkbox
                    size="small"
                    sx={{ pt: 0.25 }}
                    checked={editor?.scopes.includes(scope) ?? false}
                    onChange={(_e, checked) =>
                      setEditor((prev) =>
                        prev
                          ? { ...prev, scopes: toggleScope(prev.scopes, scope, checked) }
                          : prev,
                      )
                    }
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontSize: 13 }}>{t(`scope.${scope}`)}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                      {t(`scopeHint.${scope}`)}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={editor?.isTest ?? false}
                onChange={(_e, checked) =>
                  setEditor((prev) => (prev ? { ...prev, isTest: checked } : prev))
                }
              />
            }
            label={t("testField")}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setEditor(null)}>
            {translate("ra.action.cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={busy || !editor?.label.trim() || !editor?.scopes.length}
            onClick={() => void save()}
          >
            {translate("ra.action.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reveal-once secret */}
      <Dialog
        open={revealed !== null}
        onClose={() => setRevealed(null)}
        onClick={(e) => e.stopPropagation()}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>
          {t("revealTitle", { label: revealed?.label ?? "" })}
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 2 }}>
          <Typography sx={{ fontSize: 12.5, color: rg.danger.fg, fontWeight: 600 }}>
            {t("revealWarning")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 1.25,
              borderRadius: "10px",
              border: `1px solid ${rg.border}`,
              backgroundColor: rg.bg2,
            }}
          >
            <Typography
              sx={{
                flex: 1,
                minWidth: 0,
                fontFamily: rg.fontMono,
                fontSize: 12.5,
                wordBreak: "break-all",
              }}
            >
              {revealed?.apiKey}
            </Typography>
            <Tooltip title={t("copy")}>
              <IconButton size="small" onClick={() => void copyRevealed()}>
                <CopyIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setRevealed(null)}>
            {t("revealDone")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={confirmDelete !== null}
        onClose={() => !busy && setConfirmDelete(null)}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{ sx: { width: 420, maxWidth: "90vw" } }}
      >
        <DialogContent sx={{ textAlign: "center", pt: "24px !important" }}>
          <Typography sx={{ fontSize: "14.5px", fontWeight: 700, mb: 1 }}>
            {t("deleteTitle", { label: confirmDelete?.label ?? "" })}
          </Typography>
          <Typography sx={{ fontSize: "12.5px", color: rg.text3, lineHeight: 1.55 }}>
            {t("deleteContent")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button variant="outlined" disabled={busy} onClick={() => setConfirmDelete(null)}>
            {translate("ra.action.cancel")}
          </Button>
          <Button variant="contained" color="error" disabled={busy} onClick={() => void remove()}>
            {translate("ra.action.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AccountApiKeysDialog;
